import { riskPictureStackRank } from "./risk-picture-layers";
import type { RiskPictureLevel } from "./risk-picture-model";

/**
 * Icon is 40px. A few extra pixels so two markers that only touch still
 * count as overlapping.
 */
export const RISK_PICTURE_MARKER_COLLISION_PX = 44;

/** Thumbnails use the same icons and stack display at 24px rather than 40px. */
export const RISK_PICTURE_THUMB_MARKER_SCALE = 24 / 40;

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface RiskPictureMarkerGroup<T> {
  /** Member ids, sorted. Stable while the same markers collide. */
  id: string;
  /** Highest warning code first. That icon is the one drawn on the stack. */
  items: T[];
}

type RankedMarker = { id: string; level?: RiskPictureLevel };

/**
 * Markers whose screen positions fall within an icon of each other, including
 * through a chain (A hits B, B hits C), become one group.
 */
export const groupOverlappingRiskPictureMarkers = <T extends RankedMarker>(
  items: readonly T[],
  screenPosition: (item: T) => ScreenPoint,
  thresholdPx = RISK_PICTURE_MARKER_COLLISION_PX,
): RiskPictureMarkerGroup<T>[] => {
  const points = items.map(screenPosition);
  const parent = items.map((_, index) => index);

  const find = (index: number): number => {
    let current = index;
    while (parent[current] !== current) current = parent[current];
    return current;
  };

  const union = (left: number, right: number) => {
    const rootLeft = find(left);
    const rootRight = find(right);
    if (rootLeft !== rootRight) parent[rootRight] = rootLeft;
  };

  for (let left = 0; left < items.length; left++) {
    for (let right = left + 1; right < items.length; right++) {
      const dx = points[left].x - points[right].x;
      const dy = points[left].y - points[right].y;
      if (Math.hypot(dx, dy) <= thresholdPx) union(left, right);
    }
  }

  const buckets = new Map<number, T[]>();
  items.forEach((item, index) => {
    const root = find(index);
    const bucket = buckets.get(root);
    if (bucket) bucket.push(item);
    else buckets.set(root, [item]);
  });

  const byCode = (left: T, right: T) =>
    riskPictureStackRank(right) - riskPictureStackRank(left) ||
    left.id.localeCompare(right.id);

  return [...buckets.values()]
    .map((members) => ({
      id: [...members]
        .map((item) => item.id)
        .sort((left, right) => left.localeCompare(right))
        .join("|"),
      items: [...members].sort(byCode),
    }))
    .sort((left, right) => left.id.localeCompare(right.id));
};

/** Same overlap and severity rules as the big map, scaled to thumbnail icons. */
export const groupRiskPictureThumbMarkers = <T extends RankedMarker>(
  items: readonly T[],
  screenPosition: (item: T) => ScreenPoint,
): RiskPictureMarkerGroup<T>[] =>
  groupOverlappingRiskPictureMarkers(
    items,
    screenPosition,
    RISK_PICTURE_MARKER_COLLISION_PX * RISK_PICTURE_THUMB_MARKER_SCALE,
  );

/**
 * Pixel offsets around the shared anchor. Neighboring icons stay 44px apart,
 * which is the same gap the grouping uses, so a fan does not immediately
 * collapse back into one stack.
 */
export const spiderfyOffsets = (count: number): Array<[number, number]> => {
  if (count <= 0) return [];
  if (count === 1) return [[0, 0]];

  const radius = RISK_PICTURE_MARKER_COLLISION_PX / (2 * Math.sin(Math.PI / count));

  return Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * index) / count;
    return [Math.cos(angle) * radius, Math.sin(angle) * radius];
  });
};
